import { _ } from "../../polyglot/polyglot.js";
import { widget } from "@foretagsplatsen/widgetjs";
import ArrayStream from "../../list/streams/ArrayStream.js";
import ListSource from "../../list/ListSource.js";
import makeCardListWidget from "../list/CardListWidget/makeCardListWidget.js";

/**
 * `companyCardListWidget` displays a list of companies as cards.
 *
 * @param {predicate} spec.canAttachCompany - Returns true iff the
 * parameter (a company) can be attached
 * @param {function} spec.clientCardFactory - Factory taking a company
 * and returning a card widget
 * @param {company[]} [spec.allCompanies] - List of every company
 * which is attached or could be attached
 * @param {function} [spec.getAttachedCompanyIds] - Function returning
 * identifiers of attached companies
 * @param {function} [spec.showAttachCompany] - Function displaying a
 * dialog to let the user choose a company to attach
 */
export default widget.subclass((that, my) => {
	my.initialize = function ({
		canAttachCompany,
		clientCardFactory,
		allCompanies = [],
		getAttachedCompanyIds,
		showAttachCompany,
	}) {
		my.super(...arguments);

		my.canAttachCompany = canAttachCompany;
		my.clientCardFactory = clientCardFactory;
		my.allCompanies = allCompanies;
		my.getAttachedCompanyIds = getAttachedCompanyIds;
		my.showAttachCompany = showAttachCompany;
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.cardList = makeCardListWidget({
			itemRenderer: my.clientCardFactory,
			trailingWidget: (html) => {
				if (!my.canAttachAnyCompany()) {
					return;
				}
				my.renderTrailingCardOn(html);
			},
		});

		that.initializeListSource();
	};

	that.initializeListSource = function () {
		my.cardList.setSource(
			ListSource.makeAndFetch({
				stream: new ArrayStream({ data: my.attachedCompanies() }),
			}),
		);
	};

	that.renderContentOn = function (html) {
		if (!my.hasAttachedCompanies()) {
			my.renderEmptyOn(html);
			return;
		}

		html.render(my.cardList);
	};

	my.renderEmptyOn = function (html) {
		html.render(_("No company attached yet."));

		if (my.canAttachAnyCompany()) {
			html.render(
				" ",
				_("You may want to {link: attach} one.", {
					link: (title) =>
						html.a(
							{
								"data-test": "attach",
								click: () =>
									my.showAttachCompany(
										my.attachableCompanies(),
									),
							},
							title,
						),
				}),
			);
		}
	};

	my.renderTrailingCardOn = function (html) {
		html.div(
			{ klass: "customer-card" },
			html.div({ klass: "add-new img-circle" }),
			html.div({ klass: "customer-name" }, _("Attach company")),
		).click(() => my.showAttachCompany(my.attachableCompanies()));
	};

	/**
	 * Return true iff some company can be attached.
	 */
	my.canAttachAnyCompany = function () {
		return (
			my.attachableCompanies().length &&
			my
				.attachableCompanies()
				.some((company) => my.canAttachCompany(company))
		);
	};

	my.attachableCompanies = function () {
		const attachedCompanyIds = my.getAttachedCompanyIds();

		return my.allCompanies.filter(
			(company) => !attachedCompanyIds.includes(company.getLegacyId()),
		);
	};

	my.attachedCompanies = function () {
		const attachedCompanyIds = my.getAttachedCompanyIds();

		return my.allCompanies.filter((company) =>
			attachedCompanyIds.includes(company.getLegacyId()),
		);
	};

	my.hasAttachedCompanies = function () {
		return my.attachedCompanies().length > 0;
	};
});
